import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  CloudArrowUpIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

import { useCurrentVersion, useContentValidation } from '../../store/contentAdminStore';
import { QuestionEditor } from '../../components/admin/QuestionEditor';
import { MatrixEditor } from '../../components/admin/MatrixEditor';
import { ProfileEditor } from '../../components/admin/ProfileEditor';
import { SpiderChartEditor } from '../../components/admin/SpiderChartEditor';
import { ValidationPanel } from '../../components/admin/ValidationPanel';
import { PreviewModal } from '../../components/admin/PreviewModal';
import ReportEditor from '../../components/admin/ReportEditor';

type TabType = 'questions' | 'matrix' | 'profiles' | 'spider' | 'reports' | 'validation';

interface Tab {
  id: TabType;
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  component: React.ComponentType<any>;
}

const tabs: Tab[] = [
  {
    id: 'questions',
    name: 'Questões',
    icon: ({ className }) => (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    component: QuestionEditor,
  },
  {
    id: 'matrix',
    name: 'Matriz',
    icon: ({ className }) => (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    component: MatrixEditor,
  },
  {
    id: 'profiles',
    name: 'Perfis',
    icon: ({ className }) => (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
    component: ProfileEditor,
  },
  {
    id: 'spider',
    name: 'Gráfico',
    icon: ({ className }) => (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
      </svg>
    ),
    component: SpiderChartEditor,
  },
  {
    id: 'reports',
    name: 'Relatórios',
    icon: DocumentTextIcon,
    component: ReportEditor,
  },
  {
    id: 'validation',
    name: 'Validação',
    icon: CheckCircleIcon,
    component: ValidationPanel,
  },
];

export const ContentEditor: React.FC = () => {
  const { versionId } = useParams<{ versionId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('questions');
  const [showPreview, setShowPreview] = useState(false);

  const {
    currentVersion,
    currentContent,
    isDirty,
    isSaving,
    fetchVersion,
    fetchVersionContent,
    updateVersionContent,
    setCurrentContent,
    setDirty,
  } = useCurrentVersion();

  const {
    validationErrors,
    isValidating,
    validateContent,
    previewContent,
  } = useContentValidation();

  // Carrega dados da versão
  useEffect(() => {
    if (versionId) {
      console.log('🔄 ContentEditor: Loading version and content for versionId:', versionId);
      fetchVersion(versionId);
      fetchVersionContent(versionId);
    }
  }, [versionId, fetchVersion, fetchVersionContent]);

  // Cria conteúdo inicial se não existir
  useEffect(() => {
    console.log('🔍 ContentEditor: Checking initial content', { currentVersion, currentContent });
    if (currentVersion && !currentContent) {
      const initialContent = {
        questions: {
          titulo: 'Avaliação de Perfil de Comunicação',
          descricao: 'Questionário para identificar perfil de comunicação',
          tempo_estimado: '8-12 minutos',
          total_questoes: 0,
          instrucoes: 'Responda as questões de acordo com sua experiência',
          escala: {
            tipo: 'likert_5',
            opcoes: [
              { valor: 1, texto: 'Nunca' },
              { valor: 2, texto: 'Raramente' },
              { valor: 3, texto: 'Às vezes' },
              { valor: 4, texto: 'Frequentemente' },
              { valor: 5, texto: 'Sempre' },
            ],
          },
          dimensoes: [],
          questoes: [],
        },
        evaluation_matrix: {
          versao: '1.0',
          data_criacao: new Date().toISOString(),
          descricao: 'Matriz de avaliação para cálculo de perfis',
          algoritmo: {
            tipo: 'ponderacao_dimensional',
            descricao: 'Cálculo baseado em pesos por dimensão para cada perfil',
          },
          dimensoes: [],
          perfis: {},
          configuracao_calculo: {
            escala_minima: 1,
            escala_maxima: 5,
            formula_normalizacao: 'percentual',
            threshold_perfil_secundario: 0.15,
          },
        },
        report_contents: {
          versao: '1.0',
          data_criacao: new Date().toISOString(),
          descricao: 'Conteúdo dos relatórios de perfis',
          estrutura_relatorio: { secoes: [] },
          perfis: {},
          templates_relatorio: {
            introducao: '',
            perfil_secundario_explicacao: '',
            conclusao: '',
          },
        },
        spider_chart: {
          versao: '1.0',
          data_criacao: new Date().toISOString(),
          descricao: 'Configuração do gráfico spider para visualização de perfis',
          eixos: [],
          estilo: {
            raio_maximo: 100,
            cor_linha_grade: '#E5E7EB',
            cor_linha_perfil: '#3B82F6',
            espessura_linha_perfil: 2,
            cor_preenchimento: 'rgba(59, 130, 246, 0.1)',
            mostrar_valores: true,
            mostrar_labels: true,
            fonte_labels: 'Inter',
            tamanho_fonte_labels: 12,
          },
          configuracao_visual: {
            largura: 400,
            altura: 400,
            margem: 50,
            animacao_duracao: 1000,
            gradiente_ativo: true,
          },
        },
      };
      console.log('✅ ContentEditor: Creating initial content', initialContent);
      setCurrentContent(initialContent);
    }
  }, [currentVersion, currentContent, setCurrentContent]);

  // Auto-save quando há mudanças
  useEffect(() => {
    if (isDirty && currentContent && versionId) {
      const timeoutId = setTimeout(() => {
        updateVersionContent(versionId, currentContent);
      }, 2000); // Auto-save após 2 segundos

      return () => clearTimeout(timeoutId);
    }
  }, [isDirty, currentContent, versionId, updateVersionContent]);

  const handleSave = async () => {
    if (currentContent && versionId) {
      await updateVersionContent(versionId, currentContent);
    }
  };

  const handleValidate = async () => {
    if (versionId) {
      await validateContent(versionId);
    }
  };

  const handlePreview = async () => {
    setShowPreview(true);
  };

  const handleContentChange = (newContent: any) => {
    setDirty(true);
    setCurrentContent(newContent);
  };

  const hasErrors = validationErrors.filter(e => e.severity === 'error').length > 0;
  const hasWarnings = validationErrors.filter(e => e.severity === 'warning').length > 0;

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component;

  console.log('🎯 ContentEditor: Render state', { 
    versionId, 
    currentVersion, 
    currentContent, 
    ActiveComponent: !!ActiveComponent,
    activeTab,
    hasContent: !!currentContent 
  });

  if (!versionId) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Versão não encontrada</h3>
        <p className="text-gray-600">ID da versão não foi fornecido.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left side */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/content')}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </button>
              
              <div>
                <h1 className="text-lg font-semibold text-gray-900">
                  Editor de Conteúdo
                </h1>
                {currentVersion && (
                  <p className="text-sm text-gray-500">
                    {currentVersion.version} - {currentVersion.description}
                  </p>
                )}
              </div>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-3">
              {/* Status indicators */}
              <div className="flex items-center gap-2 text-sm">
                {isDirty && (
                  <span className="flex items-center gap-1 text-orange-600">
                    <ClockIcon className="w-4 h-4" />
                    Não salvo
                  </span>
                )}
                {isSaving && (
                  <span className="flex items-center gap-1 text-blue-600">
                    <CloudArrowUpIcon className="w-4 h-4 animate-pulse" />
                    Salvando...
                  </span>
                )}
                {!isDirty && !isSaving && (
                  <span className="flex items-center gap-1 text-green-600">
                    <CheckCircleIcon className="w-4 h-4" />
                    Salvo
                  </span>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <button
                  onClick={handleValidate}
                  disabled={isValidating}
                  className={`px-3 py-1.5 text-sm border rounded-lg transition-colors ${
                    hasErrors
                      ? 'border-red-300 text-red-700 bg-red-50'
                      : hasWarnings
                      ? 'border-yellow-300 text-yellow-700 bg-yellow-50'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {isValidating ? 'Validando...' : 'Validar'}
                </button>

                <button
                  onClick={handlePreview}
                  className="px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <EyeIcon className="w-4 h-4 inline mr-1" />
                  Preview
                </button>

                <button
                  onClick={handleSave}
                  disabled={!isDirty || isSaving}
                  className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSaving ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex space-x-8 -mb-px">
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              const Icon = tab.icon;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                    isActive
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.name}
                  
                  {/* Error/Warning indicators */}
                  {tab.id === 'validation' && hasErrors && (
                    <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                  )}
                  {tab.id === 'validation' && !hasErrors && hasWarnings && (
                    <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            {ActiveComponent && currentContent && (
              <ActiveComponent
                versionId={versionId}
                content={currentContent}
                onChange={handleContentChange}
                validationErrors={validationErrors.filter(e => 
                  e.field.startsWith(activeTab) || activeTab === 'validation'
                )}
              />
            )}
            
            {!currentContent && (
              <div className="text-center py-12">
                <div className="animate-pulse space-y-4">
                  <div className="h-8 bg-gray-200 rounded w-1/4 mx-auto"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
                  <div className="space-y-3">
                    <div className="h-4 bg-gray-200 rounded"></div>
                    <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                    <div className="h-4 bg-gray-200 rounded w-4/6"></div>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Preview Modal */}
      <PreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        versionId={versionId}
      />
    </div>
  );
};